import type WebSocket from 'ws';
import type { AcpManager } from '../agents/acp/create-acp-manager.js';
import type { FirehoseConnectionHandle } from '../firehose/connect-firehose.js';
import type { PreviewEnvironmentManager } from '../preview-environments/preview-environment-manager.js';
import { BRIDGE_CLOSE_PREVIEW_ENVIRONMENT_GRACE_MS } from '../preview-environments/manager/constants.js';
import { logImmediate, type LogFn } from '../log.js';
import { requestCliImmediateShutdown, awaitSymbolIndexWorkerShutdown } from '../runtime/cli-process-interrupt.js';
import { closeAllCliSqliteConnections } from '../sqlite/cli-database.js';
import { closeAllCodeNavCacheSqliteConnections } from '../sqlite/code-nav-cache/database.js';
import type { MainBridgeReconnectState } from './reconnect/bridge-main-reconnect.js';
import { clearReconnectQuietTimer, type ReconnectQuietSlot } from './reconnect/reconnect-quiet-slot.js';
import type { ReconnectOutageTracker } from './reconnect/reconnect-outage-plan.js';

/** Args for the firehose side-channel WebSocket; stored to reconnect if it drops while the main bridge stays up. */
export interface LastFirehoseParams {
  firehoseServerUrl: string;
  workspaceId: string;
  bridgeName: string;
  proxyPorts: number[];
}

export interface CloseableBridgeState {
  closedByUser: boolean;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  firehoseHandle: FirehoseConnectionHandle | null;
  currentWs: WebSocket | null;
  lastFirehoseParams: LastFirehoseParams | null;
  firehoseReconnectTimeout: ReturnType<typeof setTimeout> | null;
  firehoseReconnectAttempt: number;
  /** Bumped on each firehose (re)connect so stale `close` handlers do not clear the active socket. */
  firehoseGeneration: number;
  firehoseQuiet: ReconnectQuietSlot;
  firehoseOutage: ReconnectOutageTracker;
  /** Last firehose `close` while reconnecting; used after synchronous attach errors. */
  lastFirehoseReconnectCloseMeta: { code: number; reason: string } | null;
}

/**
 * Clean up all bridge resources: cancel reconnect timer, close firehose,
 * disconnect ACP agent, and close the WebSocket.
 */
export async function closeBridgeConnection(
  state: CloseableBridgeState & MainBridgeReconnectState,
  acpManager: AcpManager,
  previewEnvironmentManager?: PreviewEnvironmentManager,
  log?: LogFn
): Promise<void> {
  requestCliImmediateShutdown();
  const say = log ?? logImmediate;
  say('Cleaning up connections…');
  state.closedByUser = true;
  clearReconnectQuietTimer(state.mainQuiet);
  clearReconnectQuietTimer(state.firehoseQuiet);
  if (state.reconnectTimeout != null) {
    say('Cancelling bridge reconnect timer…');
    clearTimeout(state.reconnectTimeout);
    state.reconnectTimeout = null;
  }
  if (state.firehoseReconnectTimeout != null) {
    say('Cancelling preview tunnel reconnect timer…');
    clearTimeout(state.firehoseReconnectTimeout);
    state.firehoseReconnectTimeout = null;
  }
  if (state.firehoseHandle) {
    say('Closing preview tunnel (local HTTP proxy and dev logs)…');
    state.firehoseHandle.close();
    state.firehoseHandle = null;
  }
  say('Disconnecting local agent…');
  await acpManager.disconnect();
  if (state.currentWs) {
    say('Closing bridge connection to the cloud…');
    state.currentWs.removeAllListeners();
    const wsState = state.currentWs.readyState;
    if (wsState === 1 /* OPEN */ || wsState === 2 /* CLOSING */) {
      state.currentWs.close();
    } else {
      state.currentWs.on('error', () => {});
      state.currentWs.on('close', () => {});
    }
    state.currentWs = null;
  }
  if (previewEnvironmentManager) {
    say('Stopping local preview environment processes…');
    await previewEnvironmentManager.shutdownAllGraceful({ graceMs: BRIDGE_CLOSE_PREVIEW_ENVIRONMENT_GRACE_MS });
  }
  try {
    await awaitSymbolIndexWorkerShutdown();
    closeAllCliSqliteConnections();
    closeAllCodeNavCacheSqliteConnections();
  } catch {
    /* ignore — teardown must not throw from SQLite close */
  }
  say('Shutdown complete.');
}
